import { apiMunzen } from '@/app/api/apiMunzen/apiMunzen';
import type { EnterpriseForm } from '../types/enterpriseForm.interface';
import type { ApiResponse } from '@/common/types';
import type { Enterprise, Logo } from '../types/enterprise.interface';

/**
 * @author: Omar Sánchez Martinez
 * @since: 31/05/2025
 * @description: Verifica si el RFC ya está registrado
 */
export const uniqueRfcService = async (rfc: string, signal?: AbortSignal): Promise<boolean> => {
  const response = await apiMunzen.get<ApiResponse<{ success: boolean }>>(
    `/enterprises/unique-rfc/${rfc}`,
    { signal },
  );
  return response.data.data.success;
};

/**
 * @author: Omar Sánchez Martinez
 * @since: 05/06/2025
 * @description: Gets datos fiscales for initial configuration
 */
export const fetchEnterpriseInitialConfigurationService = async (
  token: string,
): Promise<Enterprise> => {
  const response = await apiMunzen.get<ApiResponse<Enterprise>>(
    `/new-client/datos-fiscales/${token}`,
  );
  return response.data.data;
};

/**
 * @author: Omar Sánchez Martinez
 * @since: 05/06/2025
 * @description: Fetches datos fiscales for existing enterprise
 */
export const fetchEnterpriseService = async (enterpriseId: number): Promise<Enterprise> => {
  const response = await apiMunzen.get<ApiResponse<Enterprise>>(`/datos-fiscales/${enterpriseId}`);
  return response.data.data;
};

/**
 * @author: Omar Sánchez Martinez
 * @since: 05/06/2025
 * @description: Updates datos fiscales for initial configuration
 */
export const updateEnterpriseInitialConfigurationService = async (
  token: string,
  data: EnterpriseForm,
): Promise<EnterpriseForm> => {
  const response = await apiMunzen.put<ApiResponse<Enterprise>>(
    `/new-client/datos-fiscales/${token}`,
    data,
  );
  return response.data.data;
};

/**
 * @author: Omar Sánchez Martinez
 * @since: 05/06/2025
 * @description: Updates datos fiscales for existing enterprise
 */
export const updateEnterpriseService = async (
  enterpriseId: number,
  data: EnterpriseForm,
): Promise<EnterpriseForm> => {
  const response = await apiMunzen.put<ApiResponse<Enterprise>>(
    `/datos-fiscales/${enterpriseId}`,
    data,
  );
  return response.data.data;
};

/**
 * @author: Omar Sánchez Martinez
 * @since: 05/06/2025
 * @description: Stores or updates logo for existing enterprise
 */
export const storeUpdateLogoService = async (
  enterpriseId: number,
  formData: FormData,
): Promise<Logo> => {
  const response = await apiMunzen.post<ApiResponse<Logo>>(
    `/datos-fiscales/${enterpriseId}/logo`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
  return response.data.data;
};

/**
 * @author: Omar Sánchez Martinez
 * @since: 05/06/2025
 * @description: Stores or updates logo for initial configuration
 */
export const storeUpdateLogoInitialConfigurationService = async (
  token: string,
  formData: FormData,
): Promise<Logo> => {
  const response = await apiMunzen.post<ApiResponse<Logo>>(
    `/new-client/datos-fiscales/${token}/logo`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
  return response.data.data;
};

/**
 * @author: Omar Sánchez Martinez
 * @since: 05/06/2025
 * @description: Removes logo for existing enterprise
 */
export const removeLogoService = async (enterpriseId: number): Promise<boolean> => {
  await apiMunzen.delete<ApiResponse<{ success: boolean }>>(`/datos-fiscales/${enterpriseId}/logo`);
  return true;
};

/**
 * @author: Omar Sánchez Martinez
 * @since: 05/06/2025
 * @description: Removes logo for initial configuration
 */
export const removeLogoInitialConfigurationService = async (token: string): Promise<boolean> => {
  await apiMunzen.delete<ApiResponse<{ success: boolean }>>(
    `/new-client/datos-fiscales/${token}/logo`,
  );
  return true;
};
